import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Menu,
  Store,
  ShoppingBag,
  Users,
  Settings,
  BarChart,
  Eye,
  Clock,
  LogOut,
  Crown,
  Activity,
  MessageSquare,
  Package,
  Calendar,
  Globe,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Palette,
  Layout,
  Home,
  QrCode,
  Bell,
} from "lucide-react";

import { useSidebarStore } from "@/store/sidebarStore";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useAuthStore, UserRole } from "@/store/authStore";
import { useOrderStore } from "@/store/orderStore";
import { useChatStore } from "@/store/chatStore";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { useNotificationStore } from "@/store/notificationStore";
import { BlinkingDot } from "@/components/ui/blinking-dot";
import { isSuperAdmin, isNavItemAllowed } from "@/utils/roleUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { StoreSelector } from "@/components/menu/StoreSelector";
import { fetchUserStores } from "@/services/menu/storeService";
import { useFeatureFlagStore } from "@/store/featureFlagStore";
import { useMenuStore } from "@/store/menuStore";

export function DashboardSidebar() {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { collapsed, onExpand, onCollapse } = useSidebarStore();

  const isMobile = window.matchMedia("(max-width: 768px)").matches;
  const { user, logout } = useAuthStore();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { pendingOrdersCount, activityCount, updatePendingOrdersCount } =
    useOrderStore();
  const { unreadCount, getUnreadCount } = useChatStore();
  const { unreadCount: notificationCount, getUnreadCount: getNotificationCount } = useNotificationStore();
  const {
    stores,
    selectedStoreId,
    setSelectedStoreId,
    fetchStoresByUserId,
    isFnBIndustry,
    selectedStore,
    fetchStores,
    setIsFnBIndustry,
  } = useStoreManagementStore();
  const isDevEnvironment = process.env.NODE_ENV === "development";
  const isSuperAdminUser = isSuperAdmin(user?.role as UserRole);
  const { t } = useTranslation();
  const location = useLocation();

  // Update the pending orders count when component mounts
  useEffect(() => {
    updatePendingOrdersCount();
  }, [updatePendingOrdersCount]);

  useEffect(() => {
    const loadStores = async () => {
      await fetchStores();
    };

    loadStores();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      setIsFnBIndustry(selectedStore);
    }
  }, [selectedStore, setIsFnBIndustry]);

  // Update unread chat messages count
  useEffect(() => {
    const fetchUnreadCounts = async () => {
      if (stores.length > 0) {
        // For simplicity, just get unread count for the first store
        // In a real app, you might want to aggregate counts for all stores
        await getUnreadCount(stores[0].id);
        await getNotificationCount(stores[0].id);
      }
    };

    fetchUnreadCounts();

    // Set up a timer to periodically update the unread count
    const timer = setInterval(fetchUnreadCounts, 30000); // Every 30 seconds

    return () => {
      clearInterval(timer);
    };
  }, [stores, getUnreadCount, getNotificationCount]);

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      // Prevent multiple clicks
      if (isLoggingOut) return;

      setIsLoggingOut(true);

      // Perform logout first
      await logout();

      // Only show toast and close dialog after successful logout
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
        duration: 3000,
      });

      setLogoutDialogOpen(false);
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        title: "Logout error",
        description: "There was a problem logging you out. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const isNavAllowed = (route: string) => {
    if (!user) return false;

    // Hide Menu, Tables, and QR code links for non-FnB businesses
    if (
      !isFnBIndustry &&
      [
        "menu",
        "tables",
        "qr",
        "inventory",
        "dashboard",
        "orders",
        "menu",
        "inventory",
        "landing-page",
        "super-admin",
      ].includes(route)
    ) {
      return false;
    }

    // Hide BeautyServices link for FnB businesses
    if (isFnBIndustry && route === "services") {
      return false;
    }

    return true;
  };

  return (
    <aside
      className={`fixed left-0 top-0 flex h-full flex-col border-r border-r-border-muted bg-accent transition-all duration-300 ease-in-out data-[state=open]:translate-x-0 data-[state=closed]:-translate-x-full
      ${collapsed ? "w-16" : "w-64"}
    `}
    >
      {/* Collapse button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute -right-4 top-4 z-50 h-8 w-8 rounded-full border bg-background"
        onClick={() => (collapsed ? onExpand() : onCollapse())}
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </Button>

      <div className="flex-1 overflow-y-auto py-2">
        <nav className="px-2 py-4">
          <div className={`px-4 py-2 ${collapsed ? "hidden" : "block"}`}>
            <StoreSelector
              stores={stores}
              value={selectedStoreId || ""}
              onValueChange={setSelectedStoreId}
              disabled={stores.length === 0}
              selectedStoreId={selectedStoreId}
              className="w-full"
            />
          </div>
          <div className="space-y-1">
            {isNavAllowed("dashboard") && (
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:translate-x-1 ${
                    isActive && location.pathname === "/dashboard"
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm hover:translate-x-1"
                  }`
                }
              >
                <BarChart className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                {!collapsed && <span className="ml-2">{t("dashboard")}</span>}
              </NavLink>
            )}
            {isNavAllowed("services") && (
              <NavLink
                to="/dashboard/services"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:translate-x-1 ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm"
                  }`
                }
              >
                <Sparkles className="h-4 w-4" />
                {!collapsed && (
                  <span className="ml-2">
                    {t("beautyServices.services.title")}
                  </span>
                )}
              </NavLink>
            )}

            {isNavAllowed("stores") && (
              <NavLink
                to="/dashboard/stores"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:translate-x-1 ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm hover:translate-x-1"
                  }`
                }
              >
                <Store className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                {!collapsed && (
                  <span className="ml-2">{t("stores.title")}</span>
                )}
              </NavLink>
            )}

            {isNavAllowed("tables") && (
              <NavLink
                to="/dashboard/tables"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:translate-x-1 ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm"
                  }`
                }
              >
                <QrCode className="h-4 w-4" />
                {!collapsed && <span className="ml-2">{t("qrAndTables")}</span>}
              </NavLink>
            )}
            {isNavAllowed("orders") && (
              <NavLink
                to="/dashboard/orders"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm hover:translate-x-1"
                  }`
                }
              >
                <ShoppingBag className="h-4 w-4" />
                {!collapsed && <span className="ml-2">{t("orders")}</span>}
                {pendingOrdersCount > 0 && (
                  <BlinkingDot
                    count={pendingOrdersCount}
                    className={collapsed ? "ml-0" : "ml-auto"}
                  />
                )}
              </NavLink>
            )}
            {isNavAllowed("history") && (
              <NavLink
                to="/dashboard/history"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm hover:translate-x-1"
                  }`
                }
              >
                <Clock className="h-4 w-4" />
                {!collapsed && (
                  <span className="ml-2">{t("orderHistory")}</span>
                )}
              </NavLink>
            )}
            {isNavAllowed("activity") && (
              <NavLink
                to="/dashboard/activity"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm hover:translate-x-1"
                  }`
                }
              >
                <Activity className="h-4 w-4" />
                {!collapsed && <span className="ml-2">{t("activityLog")}</span>}
                {activityCount > 0 && (
                  <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-medium text-white">
                    {activityCount}
                  </span>
                )}
              </NavLink>
            )}

            {isNavAllowed("notifications") && (
              <NavLink
                to="/dashboard/notifications"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm hover:translate-x-1"
                  }`
                }
              >
                <Bell className="h-4 w-4" />
                {!collapsed && (
                  <span className="ml-2">{t("notifications.title")}</span>
                )}
                {notificationCount > 0 && (
                  <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-500 px-1.5 text-xs font-medium text-white animate-pulse">
                    {notificationCount > 99 ? "99+" : notificationCount}
                  </span>
                )}
              </NavLink>
            )}

            {isNavAllowed("menu") && (
              <NavLink
                to="/dashboard/menu"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm hover:translate-x-1"
                  }`
                }
              >
                <Menu className="h-4 w-4" />
                {!collapsed && <span className="ml-2">{t("menu")}</span>}
              </NavLink>
            )}

            {/* New Inventory Management Link */}
            {isNavAllowed("inventory") && (
              <NavLink
                to="/dashboard/inventory"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm hover:translate-x-1"
                  }`
                }
              >
                <Package className="h-4 w-4" />
                {!collapsed && (
                  <span className="ml-2">{t("inventory.title")}</span>
                )}
              </NavLink>
            )}
            {isNavAllowed("reservations") && (
              <NavLink
                to="/dashboard/reservations"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm hover:translate-x-1"
                  }`
                }
              >
                <Calendar className="h-4 w-4" />
                {!collapsed && (
                  <span className="ml-2">{t("reservations.navlink")}</span>
                )}
              </NavLink>
            )}

            {isNavAllowed("menu-sandbox") && (
              <NavLink
                to="/dashboard/menu-sandbox"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm"
                  }`
                }
              >
                <Eye className="h-4 w-4" />
                {!collapsed && <span className="ml-2">{t("menuSandbox")}</span>}
              </NavLink>
            )}

            {isNavAllowed("storefront") && (
              <NavLink
                to="/dashboard/storefront"
                target="_blank"
                rel="noopener noreferrer"
                className={({ isActive }) =>
                  `flex items-center rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:translate-x-1 ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm"
                  }`
                }
              >
                <Layout className="h-4 w-4" />
                {!collapsed && (
                  <span className="ml-2">{t("storefront.title")}</span>
                )}
              </NavLink>
            )}
          </div>

          <div className="py-2">
            <h2
              className={`mb-2 px-4 text-lg font-semibold tracking-tight ${
                collapsed ? "hidden" : "block"
              }`}
            >
              {t("administration")}
            </h2>
            <div className="space-y-1">
              {isNavAllowed("staff") && (
                <NavLink
                  to="/dashboard/staff"
                  className={({ isActive }) =>
                    `flex items-center rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:translate-x-1 ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-md"
                        : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm"
                    }`
                  }
                >
                  <Users className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                  {!collapsed && <span className="ml-2">{t("staff")}</span>}
                </NavLink>
              )}

              {isNavAllowed("chat") && (
                <NavLink
                  to="/dashboard/chat"
                  className={({ isActive }) =>
                    `flex items-center rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:translate-x-1 ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-md"
                        : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm"
                    }`
                  }
                >
                  <MessageSquare className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                  {!collapsed && (
                    <span className="ml-2">{t("chat.title")}</span>
                  )}
                  {unreadCount > 0 && (
                    <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-medium text-white shadow-sm ring-1 ring-red-600/20 animate-pulse">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </NavLink>
              )}
              {isNavAllowed("attendance") && (
                <NavLink
                  to="/dashboard/attendance"
                  className={({ isActive }) =>
                    `flex items-center rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:translate-x-1 ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-md"
                        : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm"
                    }`
                  }
                >
                  <Users className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                  {!collapsed && (
                    <span className="ml-2">{t("attendance")}</span>
                  )}
                </NavLink>
              )}
              {isNavAllowed("settings") && (
                <NavLink
                  to="/dashboard/settings"
                  className={({ isActive }) =>
                    `flex items-center rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:translate-x-1 ${
                      isActive
                        ? "bg-primary text-primary-foreground shadow-md"
                        : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm"
                    }`
                  }
                >
                  <Settings className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                  {!collapsed && <span className="ml-2">{t("settings")}</span>}
                </NavLink>
              )}

              {/* {useFeatureFlagStore((state) => state.activateLandingPage) &&
                isNavAllowed("landing-page") && (
                  <NavLink
                    to="/dashboard/landing-page"
                    className={({ isActive }) =>
                      `flex items-center rounded-md px-3 py-2 text-sm transition-all duration-200 ease-in-out transform hover:scale-[1.02] hover:translate-x-1 ${
                        isActive
                          ? "bg-primary text-primary-foreground shadow-md"
                          : "hover:bg-muted/80 hover:text-accent-foreground hover:shadow-sm"
                      }`
                    }
                  >
                    <Globe className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                    {!collapsed && (
                      <span className="ml-2">{t("landingPage.title")}</span>
                    )}
                  </NavLink>
                )} */}

              {/* Super Admin link - Only visible to super admins */}
              {isSuperAdminUser && (
                <NavLink
                  to="/dashboard/super-admin"
                  className={({ isActive }) =>
                    `flex items-center rounded-md px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted/80 hover:text-accent-foreground"
                    }`
                  }
                >
                  <Crown className="h-4 w-4" />
                  {!collapsed && (
                    <span className="ml-2">{t("superAdmin")}</span>
                  )}
                </NavLink>
              )}
            </div>
          </div>
        </nav>
      </div>

      <div className="border-t border-border p-4">
        <Button
          variant="ghost"
          className="w-full justify-start"
          onClick={handleLogoutClick}
          disabled={isLoggingOut}
        >
          <LogOut className="h-4 w-4" />
          {!collapsed && (
            <span className="ml-2">
              {isLoggingOut ? t("loggingOut") : t("logout")}
            </span>
          )}
        </Button>
      </div>

      <AlertDialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("logoutConfirmationTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("logoutConfirmationDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoggingOut}>
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLogoutConfirm}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? t("loggingOut") : t("logout")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}
